import { digitsBefore, formatAmountInput, indexAfterDigits, parseAmount } from './number-format.utils';

/**
 * Replaces a separator-formatted money value in `el` with its raw equivalent while
 * keeping the caret on the digit the user actually clicked.
 *
 * Timing matters twice here:
 *  1. `focus` is dispatched BEFORE the browser applies the click's caret position, so
 *     the selection has to be read on a later task. Reading it synchronously in the
 *     focus handler yields 0 and sends the caret to the start of the field.
 *  2. The new value reaches the DOM through the [value] binding / writeValue(), and any
 *     write to input.value resets the selection — so the caret is restored after that.
 *
 * `commit` receives the raw (separator-free) string and is responsible for pushing it
 * into whatever holds the value — a form control, a config object, etc.
 */
export function stripSeparatorsPreservingCaret(
    el: HTMLInputElement | null | undefined,
    commit: (raw: string) => void
): void {
    if (!el) { return; }

    setTimeout(() => {
        const formatted = el.value ?? '';
        // Already raw — nothing to strip, and touching the caret here would fight the
        // user on a second focus or an ag-grid refresh() re-entering this handler.
        if (!formatted.includes(',')) { return; }

        const raw = formatAmountInput(parseAmount(formatted));
        if (raw === '') { return; }

        const selStart = el.selectionStart ?? 0;
        const selEnd = el.selectionEnd ?? selStart;
        const hadAllSelected = selStart === 0 && selEnd === formatted.length;
        const kept = digitsBefore(formatted, selStart);

        commit(raw);

        setTimeout(() => {
            // Tab-focus selects the whole field; keep it selected so typing replaces.
            if (hadAllSelected) {
                el.setSelectionRange(0, raw.length);
                return;
            }
            const pos = indexAfterDigits(raw, kept);
            el.setSelectionRange(pos, pos);
        });
    });
}
